// The live island map: terrain, water, routes and buildings merged into a few meshes,
// repeated decor and vehicles instanced, and anchors for the HTML labels.
// It only describes the scene; drawing it is the stage's job (render/stage.ts).
import {
  BoxGeometry,
  BufferAttribute,
  BufferGeometry,
  Color,
  CylinderGeometry,
  DirectionalLight,
  DodecahedronGeometry,
  DoubleSide,
  Euler,
  Group,
  HemisphereLight,
  IcosahedronGeometry,
  InstancedMesh,
  Matrix4,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  OctahedronGeometry,
  PlaneGeometry,
  Quaternion,
  Scene,
  Shape,
  ShapeGeometry,
  SphereGeometry,
  Vector3,
  ConeGeometry,
  type Material,
} from 'three';
import { countParts, CROWN_RADIUS, multiplyDecor, placeDecor, type DecorKind, type DecorObject, type ExtraPuff } from '../world/decor';
import {
  parseKey,
  SEA_Y,
  TERRAIN,
  terrainAt,
  topAt,
  worldX,
  worldZ,
  type Island,
  type Point,
  type ResourceKind,
  type TerrainCode,
} from '../world/island';
import { routePath } from '../world/paths';
import { forEachTrafficPose, planTraffic, trafficPoses, vehiclesPerCopy, type TrafficRoute, type VehicleKind } from '../world/traffic';
import { createBatchMaterial, GeometryBatch } from './batch';
import { C, SKIRT } from './palette';
import { box, building, gate, rocket, vehicleGeometry } from './prefabs';

/** Copies of every decor object and vehicle in the stress mode. */
export const STRESS_FACTOR = 5;

export type LabelAnchor =
  | { readonly kind: 'lock'; readonly position: Vector3; readonly price: number }
  | { readonly kind: 'buy'; readonly position: Vector3; readonly price: number }
  | { readonly kind: 'landmark'; readonly position: Vector3; readonly text: string }
  | { readonly kind: 'rate'; readonly position: Vector3; readonly value: number; readonly resource: ResourceKind; readonly name: string };

/** Sample production figures of the mockup; the map shows the simulation's real figures from H3. */
const SAMPLE_RATES: Record<string, readonly [ResourceKind, number]> = {
  mina: ['mineral', 18],
  fundicion: ['hierro', 40],
  taller: ['circuito', 12],
  lab: ['ciencia', 4],
  bomba: ['agua', 30],
  muelle: ['mineral', 20],
  islote: ['mineral', 20],
};

const SIDES: Record<'N' | 'S' | 'E' | 'W', readonly [number, number]> = { N: [0, -1], S: [0, 1], E: [1, 0], W: [-1, 0] };

/** Fixed point the sun aims at; shadows only depend on the island, never on the camera. */
const SUN_TARGET = new Vector3(0.2, -0.3, 0.9);
const UP = new Vector3(0, 1, 0);
const ONE = new Vector3(1, 1, 1);

export interface SceneCounts {
  readonly decor: Record<DecorKind, number>;
  readonly vehicles: number;
}

export interface IslandSceneOptions {
  readonly stress?: boolean;
  readonly shadows?: boolean;
}

export class IslandScene {
  readonly scene = new Scene();
  readonly sun = new DirectionalLight(C.sun, 3.1);
  readonly labels: LabelAnchor[] = [];
  /** World-space box that holds the whole island, for camera limits. */
  readonly bounds: { readonly minX: number; readonly maxX: number; readonly minZ: number; readonly maxZ: number };

  private readonly island: Island;
  private readonly batchMaterial = createBatchMaterial();
  private readonly materials: Material[] = [this.batchMaterial];
  private readonly baseDecor: DecorObject[];
  private readonly traffic: TrafficRoute[];
  private readonly decorGroup = new Group();
  private readonly vehicleMeshes = new Map<VehicleKind, InstancedMesh>();
  private readonly vehicleGeometries = new Map<VehicleKind, BufferGeometry>();
  private readonly vehicleCursor = new Map<VehicleKind, number>();
  private readonly vehicleScratch = { matrix: new Matrix4(), position: new Vector3(), rotation: new Quaternion() };
  private stressOn = false;
  private counts: SceneCounts;

  constructor(island: Island, opts: IslandSceneOptions = {}) {
    this.island = island;
    this.bounds = { minX: -island.cols / 2, maxX: island.cols / 2, minZ: -island.rows / 2, maxZ: island.rows / 2 };
    this.scene.background = new Color(C.sky);
    this.scene.add(new HemisphereLight(C.skyLight, C.groundLight, 1.05));
    this.setupSun();

    this.buildSea();
    const solid = new GeometryBatch();
    const flat = new GeometryBatch();
    this.buildTerrain(solid, flat);
    this.buildRoutes(solid, flat);
    const puffs = this.buildBuildings(solid);
    this.buildLandmarks(solid);
    this.addBatch(solid, { cast: true, name: 'isla' });
    this.addBatch(flat, { cast: false, name: 'suelo' });

    this.baseDecor = placeDecor(island, puffs);
    this.traffic = planTraffic(island);
    this.scene.add(this.decorGroup);
    this.counts = { decor: countParts(this.baseDecor), vehicles: 0 };
    this.stressOn = opts.stress ?? false;
    this.rebuildDynamic();
    this.setShadows(opts.shadows ?? true);
    this.update(0);
  }

  /** Number of decor parts per kind and vehicles currently in the scene. */
  get objectCounts(): SceneCounts {
    return this.counts;
  }

  setStress(on: boolean): void {
    if (on === this.stressOn) return;
    this.stressOn = on;
    this.rebuildDynamic();
  }

  setShadows(on: boolean): void {
    this.sun.castShadow = on;
  }

  /** Moves the vehicles to their poses at `seconds` of ambient time. */
  update(seconds: number): void {
    const copies = this.stressOn ? STRESS_FACTOR : 1;
    for (const kind of this.vehicleCursor.keys()) this.vehicleCursor.set(kind, 0);
    forEachTrafficPose(this.island, this.traffic, seconds, copies, this.placeVehicle);
    for (const mesh of this.vehicleMeshes.values()) {
      mesh.instanceMatrix.needsUpdate = true;
      mesh.computeBoundingSphere();
    }
  }

  private readonly placeVehicle = (kind: VehicleKind, x: number, y: number, z: number, angle: number): void => {
    const mesh = this.vehicleMeshes.get(kind);
    if (!mesh) return;
    const i = this.vehicleCursor.get(kind) ?? 0;
    this.vehicleCursor.set(kind, i + 1);
    const { matrix, position, rotation } = this.vehicleScratch;
    mesh.setMatrixAt(i, matrix.compose(position.set(x, y, z), rotation.setFromAxisAngle(UP, angle), ONE));
  };

  dispose(): void {
    this.scene.traverse((o) => {
      if (o instanceof Mesh) (o.geometry as BufferGeometry).dispose();
    });
    for (const g of this.vehicleGeometries.values()) g.dispose();
    for (const m of this.materials) m.dispose();
  }

  // ------------------------------------------------------------ setup ---

  private setupSun(): void {
    // Sun from the upper left: shadows fall to the lower right, as in the reference.
    this.sun.position.set(SUN_TARGET.x - 6, 12, SUN_TARGET.z - 3);
    this.sun.target.position.copy(SUN_TARGET);
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0006;
    this.sun.shadow.normalBias = 0.02;
    this.sun.shadow.radius = 3;
    const cam = this.sun.shadow.camera;
    cam.left = -11;
    cam.right = 11;
    cam.top = 11;
    cam.bottom = -11;
    cam.near = 1;
    cam.far = 40;
    cam.updateProjectionMatrix();
    this.scene.add(this.sun, this.sun.target);
  }

  private material<T extends Material>(m: T): T {
    this.materials.push(m);
    return m;
  }

  private addBatch(batch: GeometryBatch, opts: { cast: boolean; name: string }): void {
    const mesh = new Mesh(batch.build(), this.batchMaterial);
    mesh.name = opts.name;
    mesh.castShadow = opts.cast;
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
  }

  private addStatic(mesh: Mesh, name: string): Mesh {
    mesh.name = name;
    mesh.updateMatrix();
    mesh.matrixAutoUpdate = false;
    this.scene.add(mesh);
    return mesh;
  }

  // ------------------------------------------------------------- sea ---

  private buildSea(): void {
    const size = 80;
    const deep = new Mesh(new PlaneGeometry(size, size), this.material(new MeshBasicMaterial({ color: C.seaDeep })));
    deep.rotation.x = -Math.PI / 2;
    deep.position.y = SEA_Y - 3.4;
    this.addStatic(deep, 'fondo');
    const surface = new Mesh(
      new PlaneGeometry(size, size),
      this.material(new MeshStandardMaterial({ color: C.sea, transparent: true, opacity: 0.38, roughness: 0.4, depthWrite: false })),
    );
    surface.rotation.x = -Math.PI / 2;
    surface.position.y = SEA_Y;
    surface.receiveShadow = true;
    surface.renderOrder = 2;
    this.addStatic(surface, 'mar');
  }

  // --------------------------------------------------------- terrain ---

  private buildTerrain(solid: GeometryBatch, flat: GeometryBatch): void {
    const island = this.island;
    const grassLayer = 0.2;
    const columnQuads: [number, number, number, number][] = [];
    const water = new GeometryBatch();
    const skirtCache = new Map<number, BufferGeometry>();
    const skirt = (len: number): BufferGeometry => {
      const cached = skirtCache.get(len);
      if (cached) return cached;
      const s = new Shape();
      const n = Math.round(len / 0.06);
      s.moveTo(0, 0);
      for (let i = 0; i < n; i++) {
        s.lineTo(((i + 0.5) * len) / n, -0.07 - ((i * 37) % 5) * 0.012);
        s.lineTo(((i + 1) * len) / n, 0);
      }
      s.lineTo(len, 0.005);
      s.lineTo(0, 0.005);
      const geo = new ShapeGeometry(s);
      geo.translate(-len / 2, 0, 0);
      skirtCache.set(len, geo);
      return geo;
    };
    const white = new Color('#ffffff');

    for (const [key, code] of island.terrain) {
      const [c, r] = parseKey(key);
      const x = worldX(island, c);
      const z = worldZ(island, r);
      const t = TERRAIN[code].top;
      const palette = terrainColors(code);
      let color = palette[(c + r) % 2] as string;
      if (island.fog.has(key)) color = '#' + new Color(color).lerp(white, 0.18).getHexString();
      solid.add(box(1, grassLayer, 1), color, { position: [x, t - grassLayer / 2, z] });
      const h2 = Math.max(0.02, t - grassLayer - SEA_Y);
      flat.add(box(0.995, h2, 0.995), C.sandLayer, { position: [x, SEA_Y + h2 / 2, z] });

      for (const [dc, dr] of Object.values(SIDES)) {
        const neighbour = terrainAt(island, c + dc, r + dr);
        if (neighbour && TERRAIN[neighbour].top >= t - 0.001) continue;
        // Tufted grass edge on faces that drop to a lower neighbour or to the sea.
        flat.add(skirt(1), SKIRT[code] ?? C.skirt, {
          position: [x + dc * 0.504, t, z + dr * 0.504],
          rotation: [0, Math.atan2(dc, dr), 0],
        });
        if (!neighbour) {
          // White foam at the waterline and the translucent column below the sea.
          flat.add(box(dc ? 0.03 : 1.03, 0.02, dr ? 0.03 : 1.03), C.foam, { position: [x + dc * 0.51, SEA_Y + 0.005, z + dr * 0.51] });
          const ex = x + dc / 2;
          const ez = z + dr / 2;
          const px = dr ? 0.5 : 0;
          const pz = dc ? 0.5 : 0;
          columnQuads.push([ex - px, ez - pz, ex + px, ez + pz]);
        }
      }

      if (TERRAIN[code].water) {
        water.add(box(1.001, 0.16, 1.001), C.water, { position: [x, -0.2, z] });
      }
      if (code === 't') {
        flat.add(new CylinderGeometry(0.05, 0.07, 0.02, 7), '#ffb347', { position: [x + 0.12, t + 0.01, z + 0.05] }, { emissive: '#ff7a00', emissiveIntensity: 0.8 });
      }
    }
    for (const g of skirtCache.values()) g.dispose();

    const waterMesh = new Mesh(water.build(), this.material(new MeshStandardMaterial({ vertexColors: true, transparent: true, opacity: 0.82, roughness: 0.3 })));
    waterMesh.receiveShadow = true;
    this.addStatic(waterMesh, 'agua');

    // Translucent columns: vertical quads fading with depth.
    const depth = 3.2;
    const pos: number[] = [];
    const col: number[] = [];
    const idx: number[] = [];
    columnQuads.forEach(([x1, z1, x2, z2], i) => {
      const k = i * 4;
      pos.push(x1, SEA_Y, z1, x2, SEA_Y, z2, x1, SEA_Y - depth, z1, x2, SEA_Y - depth, z2);
      col.push(1, 1, 1, 0.85, 1, 1, 1, 0.85, 1, 1, 1, 0.05, 1, 1, 1, 0.05);
      idx.push(k, k + 2, k + 1, k + 1, k + 2, k + 3);
    });
    const geo = new BufferGeometry();
    geo.setAttribute('position', new BufferAttribute(new Float32Array(pos), 3));
    geo.setAttribute('color', new BufferAttribute(new Float32Array(col), 4));
    geo.setIndex(idx);
    const columns = new Mesh(
      geo,
      this.material(new MeshBasicMaterial({ color: C.column, vertexColors: true, transparent: true, side: DoubleSide, depthWrite: false })),
    );
    columns.renderOrder = 1;
    this.addStatic(columns, 'columnas');
  }

  // ---------------------------------------------------------- routes ---

  private buildRoutes(solid: GeometryBatch, flat: GeometryBatch): void {
    const island = this.island;
    const top = (x: number, z: number): number => topAt(island, x, z);
    const yLand = (x: number, z: number): number => Math.max(top(x, z), 0) + 0.012;
    for (const route of island.routes) {
      const pts = routePath(island, route);
      if (route.kind === 'road') {
        ribbon(flat, pts, 0.2, '#e3c98d', (x, z) => top(x, z) + 0.008);
        ribbon(flat, pts, 0.14, '#efdcaa', (x, z) => top(x, z) + 0.012);
      } else if (route.kind === 'rail') {
        ribbon(flat, pts, 0.24, '#d9c79a', yLand);
        for (let i = 0; i < pts.length; i += 2) {
          const a = pts[Math.max(0, i - 1)] as Point;
          const b = pts[Math.min(pts.length - 1, i + 1)] as Point;
          const [px, pz] = pts[i] as Point;
          flat.add(box(0.22, 0.02, 0.035), '#8a5a3c', { position: [px, yLand(px, pz) + 0.01, pz], rotation: [0, Math.atan2(b[0] - a[0], b[1] - a[1]) + Math.PI / 2, 0] });
        }
        for (const offset of [-0.055, 0.055]) ribbon(flat, pts, 0.022, '#5b5f68', (x, z) => yLand(x, z) + 0.03, offset);
        // Bridge where the line crosses the river.
        for (const [px, pz] of pts) {
          const code = terrainAt(island, Math.floor(px + island.cols / 2), Math.floor(pz + island.rows / 2));
          if (code === 'l' || code === 'r') solid.add(box(0.06, 0.03, 0.3), '#9c6a43', { position: [px, 0, pz] });
        }
      } else if (route.kind === 'boat' || route.kind === 'barge') {
        const y = route.kind === 'boat' ? SEA_Y + 0.01 : -0.11;
        for (let i = 0; i < pts.length; i += 2) {
          const [px, pz] = pts[i] as Point;
          flat.add(new SphereGeometry(0.022, 6, 4), '#ffffff', { position: [px, y, pz] }, { smooth: true });
        }
      } else {
        pts.forEach(([px, pz], i) => {
          if (i % 2) return;
          const t = i / (pts.length - 1);
          flat.add(new SphereGeometry(0.018, 6, 4), '#ffffff', { position: [px, 0.75 + Math.sin(t * Math.PI) * 0.35, pz] }, { smooth: true, emissive: '#ffffff', emissiveIntensity: 0.4 });
        });
      }
    }
  }

  // ------------------------------------------------------- buildings ---

  private buildBuildings(solid: GeometryBatch): ExtraPuff[] {
    const island = this.island;
    const puffs: ExtraPuff[] = [];
    for (const tile of island.tiles) {
      const first = tile.cells[0];
      if (!first) continue;
      const code = terrainAt(island, first[0], first[1]) as TerrainCode;
      puffs.push(...building(solid, island, tile, TERRAIN[code].top));
    }
    for (const tile of island.tiles) {
      for (const port of tile.ports) {
        const [c, r] = port.cell;
        const d = SIDES[port.side];
        const code = terrainAt(island, c, r) as TerrainCode;
        gate(solid, worldX(island, c) + d[0] * 0.45, TERRAIN[code].top, worldZ(island, r) + d[1] * 0.45, d, port.kind);
      }
    }
    return puffs;
  }

  private buildLandmarks(solid: GeometryBatch): void {
    const island = this.island;
    const [gc, gr] = island.goal;
    const goalTop = TERRAIN[terrainAt(island, gc, gr) as TerrainCode].top;
    const gx = worldX(island, gc);
    const gz = worldZ(island, gr);
    rocket(solid, gx, goalTop, gz);
    this.labels.push({ kind: 'landmark', position: new Vector3(gx, goalTop + 1.0, gz), text: 'Proyecto Lanzamiento' });

    // The tile the player is about to buy: a translucent blue column with white corners.
    const [bc, br] = parseKey(island.buyTarget);
    const bx = worldX(island, bc);
    const bz = worldZ(island, br);
    const column = new Mesh(
      new BoxGeometry(1, 0.9, 1),
      this.material(new MeshStandardMaterial({ color: '#7fc4ff', transparent: true, opacity: 0.32, emissive: '#3aa0ff', emissiveIntensity: 0.4, depthWrite: false })),
    );
    column.position.set(bx, 0.42, bz);
    column.renderOrder = 3;
    this.addStatic(column, 'compra');
    const corner = { emissive: '#ffffff', emissiveIntensity: 0.6 };
    for (const sx of [-1, 1]) {
      for (const sz of [-1, 1]) {
        solid.add(box(0.18, 0.04, 0.04), '#ffffff', { position: [bx + sx * 0.41, 0.9, bz + sz * 0.48] }, corner);
        solid.add(box(0.04, 0.04, 0.18), '#ffffff', { position: [bx + sx * 0.48, 0.9, bz + sz * 0.41] }, corner);
      }
    }
    this.labels.push({ kind: 'buy', position: new Vector3(bx, 1.12, bz), price: island.locks.get(island.buyTarget) ?? 0 });

    for (const [key, price] of island.locks) {
      if (key === island.buyTarget) continue;
      const [c, r] = parseKey(key);
      const code = terrainAt(island, c, r) as TerrainCode;
      this.labels.push({ kind: 'lock', position: new Vector3(worldX(island, c), TERRAIN[code].top + 0.3, worldZ(island, r)), price });
    }

    for (const tile of island.tiles) {
      const rate = SAMPLE_RATES[tile.id];
      const first = tile.cells[0];
      if (!rate || !first) continue;
      const [c, r] = tile.id === 'taller' ? [9.5, 5.6] : first;
      const code = terrainAt(island, Math.floor(c), Math.floor(r)) as TerrainCode;
      this.labels.push({
        kind: 'rate',
        position: new Vector3(worldX(island, c), TERRAIN[code].top + 0.78, worldZ(island, r)),
        value: rate[1],
        resource: rate[0],
        name: tile.name,
      });
    }
  }

  // ----------------------------------------------- instanced content ---

  private rebuildDynamic(): void {
    for (const child of [...this.decorGroup.children]) {
      this.decorGroup.remove(child);
      if (child instanceof InstancedMesh) child.dispose();
    }
    const copies = this.stressOn ? STRESS_FACTOR : 1;
    const decor = copies > 1 ? multiplyDecor(this.island, this.baseDecor, copies) : this.baseDecor;
    const byKind = new Map<DecorKind, ReturnType<typeof flattenParts>>();
    for (const part of flattenParts(decor)) {
      const list = byKind.get(part.kind) ?? [];
      list.push(part);
      byKind.set(part.kind, list);
    }
    const m = new Matrix4();
    const q = new Quaternion();
    const e = new Euler();
    const color = new Color();
    for (const [kind, parts] of byKind) {
      const proto = this.decorPrototype(kind);
      const mesh = new InstancedMesh(proto.geometry, proto.material, parts.length);
      mesh.name = `decor-${kind}`;
      mesh.castShadow = proto.cast;
      mesh.receiveShadow = true;
      parts.forEach((p, i) => {
        q.setFromEuler(e.set(p.rx, p.ry, p.rz));
        mesh.setMatrixAt(i, m.compose(new Vector3(p.x, p.y, p.z), q, new Vector3(p.sx, p.sy, p.sz)));
        mesh.setColorAt(i, color.set(p.color));
      });
      mesh.computeBoundingSphere();
      this.decorGroup.add(mesh);
    }
    this.counts = { decor: countParts(decor), vehicles: vehiclesPerCopy(this.traffic) * copies };

    // Vehicles: one instanced mesh per kind, filled by update().
    this.vehicleMeshes.clear();
    const perKind = new Map<VehicleKind, number>();
    for (const p of trafficPoses(this.island, this.traffic, 0, copies)) perKind.set(p.kind, (perKind.get(p.kind) ?? 0) + 1);
    for (const [kind, count] of perKind) {
      let geometry = this.vehicleGeometries.get(kind);
      if (!geometry) {
        geometry = vehicleGeometry(kind);
        this.vehicleGeometries.set(kind, geometry);
      }
      const mesh = new InstancedMesh(geometry, this.batchMaterial, count);
      mesh.name = `vehiculo-${kind}`;
      mesh.receiveShadow = true;
      this.vehicleMeshes.set(kind, mesh);
      this.decorGroup.add(mesh);
    }
  }

  private readonly prototypes = new Map<DecorKind, { geometry: BufferGeometry; material: Material; cast: boolean }>();

  private decorPrototype(kind: DecorKind): { geometry: BufferGeometry; material: Material; cast: boolean } {
    const cached = this.prototypes.get(kind);
    if (cached) return cached;
    const facet = (): Material => this.material(new MeshStandardMaterial({ roughness: 0.9, metalness: 0, flatShading: true }));
    let proto: { geometry: BufferGeometry; material: Material; cast: boolean };
    switch (kind) {
      case 'trunk':
        proto = { geometry: new CylinderGeometry(0.022, 0.032, 0.26, 5), material: facet(), cast: true };
        break;
      case 'crown':
        proto = { geometry: new IcosahedronGeometry(CROWN_RADIUS, 0), material: facet(), cast: true };
        break;
      case 'boulder':
      case 'pebble':
        proto = { geometry: new DodecahedronGeometry(1, 0), material: facet(), cast: true };
        break;
      case 'ore':
        proto = {
          geometry: new OctahedronGeometry(0.035, 0),
          material: this.material(new MeshStandardMaterial({ roughness: 0.9, flatShading: true, emissive: '#6a2a0a', emissiveIntensity: 0.3 })),
          cast: true,
        };
        break;
      case 'tuft':
        proto = { geometry: new ConeGeometry(0.012, 0.09, 3), material: facet(), cast: false };
        break;
      case 'cloud':
        proto = {
          geometry: new IcosahedronGeometry(1, 1),
          material: this.material(new MeshStandardMaterial({ roughness: 0.9, transparent: true, opacity: 0.92 })),
          cast: false,
        };
        break;
      case 'sparkle':
        proto = {
          geometry: new PlaneGeometry(1, 0.012),
          material: this.material(new MeshStandardMaterial({ roughness: 0.9, transparent: true, opacity: 0.6 })),
          cast: false,
        };
        break;
    }
    this.prototypes.set(kind, proto);
    return proto;
  }
}

function flattenParts(objects: readonly DecorObject[]): DecorObject['parts'][number][] {
  return objects.flatMap((o) => o.parts);
}

function terrainColors(code: TerrainCode): readonly string[] {
  switch (code) {
    case 'p':
      return C.grass;
    case 'b':
      return C.forest;
    case 'a':
      return C.sand;
    case 'h':
    case 'c':
    case 'm':
      return C.rock;
    case 't':
      return C.thermal;
    case 'l':
    case 'r':
      return [C.riverbed, C.riverbed];
  }
}

/** A flat strip along a polyline at height `y(x, z)`, facing up. */
function ribbon(batch: GeometryBatch, pts: readonly Point[], width: number, color: string, y: (x: number, z: number) => number, offset = 0): void {
  const left: Vector3[] = [];
  const right: Vector3[] = [];
  for (let i = 0; i < pts.length; i++) {
    const a = pts[Math.max(0, i - 1)] as Point;
    const b = pts[Math.min(pts.length - 1, i + 1)] as Point;
    let tx = b[0] - a[0];
    let tz = b[1] - a[1];
    const l = Math.hypot(tx, tz) || 1;
    tx /= l;
    tz /= l;
    const nx = -tz;
    const nz = tx;
    const [px, pz] = pts[i] as Point;
    const cx = px + nx * offset;
    const cz = pz + nz * offset;
    const h = y(px, pz);
    left.push(new Vector3(cx + (nx * width) / 2, h, cz + (nz * width) / 2));
    right.push(new Vector3(cx - (nx * width) / 2, h, cz - (nz * width) / 2));
  }
  const tris: number[] = [];
  const pushUp = (p: Vector3, q: Vector3, r: Vector3): void => {
    // Keep every triangle facing up so the single-sided batch material shows it.
    const n = new Vector3().subVectors(q, p).cross(new Vector3().subVectors(r, p));
    const [b, c] = n.y >= 0 ? [q, r] : [r, q];
    tris.push(p.x, p.y, p.z, b.x, b.y, b.z, c.x, c.y, c.z);
  };
  for (let i = 1; i < left.length; i++) {
    pushUp(left[i - 1] as Vector3, right[i - 1] as Vector3, left[i] as Vector3);
    pushUp(right[i - 1] as Vector3, right[i] as Vector3, left[i] as Vector3);
  }
  batch.addTriangles(tris, color, { smooth: true });
}
